/**
 * Opponent List
 * Every seat at the table in seat order 1-10, the viewer's own included
 * (shown as "You"), with the "1st" chip on whoever opens this game's
 * betting rounds: avatar, status,
 * their 2 hole cards (face-down/Oracle-masked/real, however PlayingCard
 * already resolves ViewerFacingCard), chip stack, and a current-bet token.
 * The usual vertically-scrolling column of full-width rows (landscape,
 * desktop); or, on a phone held upright, a column of compact tiles beside
 * the grid (variant "tiles": seats 1-5 one side, 6-10 the other).
 */

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Player } from '../../store/types';
import { formatChips } from '../../utils/format';
import PlayingCard from './PlayingCard';
import SpecialCard from './SpecialCard';
import { specialOfCard } from './specialOfCard';

interface OpponentListProps {
  /** Every seat (the viewer's own included), in seat order — null for an empty seat. */
  seats: Array<{ seatPosition: number; player: Player | null }>;
  /** The viewer's own seat, shown as "You" (null while spectating). */
  yourSeat?: number | null;
  /** Player ids who won the rank-axis ("Best Hand") half of the pot — tracked separately from jewelWinnerIds so a jewel-only winner is never lost in a single generic "winner" flag. */
  rankWinnerIds?: Set<string>;
  /** Player ids who won the jewel-axis ("Best Color") half of the pot. */
  jewelWinnerIds?: Set<string>;
  /**
   * "tiles": compact tiles filling a column beside the grid (phone, upright).
   * "column": full rows sharing a column's height beside the grid (computer).
   */
  variant?: 'rows' | 'tiles' | 'column';
}

const STATUS_BORDER: Record<string, string> = {
  ACTIVE: 'border-green-500',
  FOLDED: 'border-gray-600',
  ALL_IN: 'border-yellow-500',
  SITTING_OUT: 'border-gray-600',
  DISCONNECTED: 'border-red-500',
};

const STATUS_LABEL: Record<string, string> = {
  FOLDED: 'Folded',
  ALL_IN: 'All In',
  SITTING_OUT: 'Sitting Out',
  DISCONNECTED: 'Disconnected',
};

const OpponentRow: React.FC<{
  seatPosition: number;
  player: Player | null;
  wonRank?: boolean;
  wonJewel?: boolean;
  isYou?: boolean;
  compact?: boolean;
  /** Share the column's height with the other rows (variant "column"). */
  fill?: boolean;
}> = ({ seatPosition, player, wonRank, wonJewel, isYou, compact, fill }) => {
  // The list scrolls (10 rows don't always fit), so whoever's turn it is
  // gets scrolled into view — otherwise a player acting in a row below the
  // fold is invisible, and it looks as if you're first to act.
  const rowRef = useRef<HTMLDivElement>(null);
  const isTurn = !!player?.isCurrentTurn;
  useEffect(() => {
    if (isTurn) rowRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [isTurn]);

  if (compact) {
    return <SeatTile seatPosition={seatPosition} player={player} wonRank={wonRank} wonJewel={wonJewel} isYou={isYou} />;
  }

  if (!player) {
    return (
      <div
        data-testid="empty-seat"
        data-seat={seatPosition + 1}
        className={`${fill ? 'flex-1 min-h-0' : 'flex-shrink-0'} min-w-[130px] landscape:min-w-0 landscape:w-full flex items-center gap-2 p-2 rounded-lg border border-dashed border-gray-700 bg-gray-900/30`}
      >
        <div className="w-9 h-9 rounded-full border-2 border-dashed border-gray-700 flex-shrink-0" />
        <div className="text-xs text-gray-600 truncate">Empty seat {seatPosition + 1}</div>
      </div>
    );
  }

  const isWinner = wonRank || wonJewel;
  const statusLabel = STATUS_LABEL[player.status];

  return (
    <motion.div
      ref={rowRef}
      // Test markers (gameplay-sync e2e test) — no visual effect.
      data-testid="player-row"
      data-player-id={player.id}
      data-seat={seatPosition + 1}
      data-current-turn={player.isCurrentTurn ? 'true' : 'false'}
      data-status={player.status}
      data-chip={player.hasDealer ? 'true' : 'false'}
      className={`relative ${fill ? 'flex-1 min-h-0 justify-center overflow-hidden' : 'flex-shrink-0'} min-w-[130px] landscape:min-w-0 landscape:w-full flex flex-col gap-1 p-2 rounded-lg border transition-colors ${
        isWinner
          ? 'border-yellow-400 bg-yellow-900/30 ring-2 ring-yellow-400/60'
          : player.isCurrentTurn
          ? 'border-yellow-400 bg-yellow-950/30'
          : 'border-gray-800 bg-gray-900/50'
      }`}
      animate={player.isCurrentTurn && !isWinner ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 1, repeat: player.isCurrentTurn && !isWinner ? Infinity : 0 }}
    >
      <div className="flex items-center gap-2">
        {/* Avatar */}
        <div
          className={`relative w-9 h-9 rounded-full border-2 ${
            STATUS_BORDER[player.status] || 'border-gray-600'
          } bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center overflow-hidden flex-shrink-0`}
        >
          {player.avatar ? (
            <img src={player.avatar} alt={player.username} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-white">{player.username.charAt(0).toUpperCase()}</span>
          )}
          {!player.isConnected && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-red-500 text-[10px]">
              ⚠
            </div>
          )}
          {/* Two distinct badges — a jewel-axis win is never folded into a
              single generic "winner" flag, so it stays visible even when a
              different player won the rank axis (or the same player won
              both, in which case both badges stack here). */}
          {(wonRank || wonJewel) && (
            <div className="absolute -top-1.5 -right-1.5 flex gap-0.5 text-xs leading-none">
              {wonRank && <span title="Won Best Hand">🏆</span>}
              {wonJewel && <span title="Won Best Color">💎</span>}
            </div>
          )}
        </div>

        {/* Name + status + chips */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-gray-500 flex-shrink-0">{seatPosition + 1}</span>
            <div className={`text-xs font-semibold truncate ${isYou ? 'text-yellow-300' : 'text-white'}`}>
              {isYou ? 'You' : player.username}
            </div>
            {/* The chip: this player opens every betting round of the hand. */}
            {player.hasDealer && (
              <span
                title="Acts first this game"
                className="h-4 px-1 rounded-full bg-yellow-500 border border-yellow-200 text-black text-[8px] font-bold flex items-center justify-center flex-shrink-0 shadow"
              >
                1st
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="text-[10px] text-yellow-400 font-semibold">{formatChips(player.chipCount)}</span>
            {statusLabel && <span className="text-[10px] text-gray-500">{statusLabel}</span>}
          </div>
        </div>

        {/* Current-bet token */}
        {!!player.currentBet && player.currentBet > 0 && (
          <motion.div
            className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold shadow"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
          >
            {formatChips(player.currentBet)}
          </motion.div>
        )}

        {/* Hole cards */}
        <div className="flex gap-0.5 flex-shrink-0">
          {/* Each special on its own card's side (left card's to the left, right card's to the right). */}
          {(() => {
            const hole = (player.holeCards ?? [null, null]).slice(0, 2);
            const [left, right] = [specialOfCard(hole[0]), specialOfCard(hole[1])];
            return (
              <>
                {left && <SpecialCard key="special-0" special={left} showName={false} className="w-6" />}
                {hole.map((card, i) => (
                  <PlayingCard key={i} card={card ?? undefined} faceDown={!card} className="w-6" />
                ))}
                {right && <SpecialCard key="special-1" special={right} showName={false} className="w-6" />}
              </>
            );
          })()}
        </div>
      </div>

      {/* Revealed hand at showdown — sits directly under this player's own
          row, not a separate banner, so nothing else on screen moves. Each
          axis colored independently (yellow = won Best Hand, fuchsia = won
          Best Color — same accent colors PlayerPanel uses for those two
          boxes) so which axis this player actually won stays visible even
          when only one of the two applies. */}
      {(player.handDescription || player.jewelHandDescription) && (
        // One line each, wrapping rather than truncating — the end of the
        // description (high card / kickers) is what shows why a hand won.
        <div className="text-[10px] font-semibold leading-tight pl-0.5 flex flex-col gap-0.5">
          {player.handDescription && (
            <span className={wonRank ? 'text-yellow-300' : 'text-gray-400'}>
              🏆 {player.handDescription}
            </span>
          )}
          {player.jewelHandDescription && (
            <span className={wonJewel ? 'text-fuchsia-300' : 'text-gray-400'}>
              💎 {player.jewelHandDescription}
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
};

/**
 * One seat as a small tile, for the columns beside the grid on a phone held
 * upright: name, chips and status, the two cards (and any special shown),
 * the bet this round, the 1st chip and win badges. Same test markers as
 * the full row.
 */
const SeatTile: React.FC<{
  seatPosition: number;
  player: Player | null;
  wonRank?: boolean;
  wonJewel?: boolean;
  isYou?: boolean;
}> = ({ seatPosition, player, wonRank, wonJewel, isYou }) => {
  if (!player) {
    return (
      <div
        data-testid="empty-seat"
        data-seat={seatPosition + 1}
        className="flex-1 min-h-0 flex items-center justify-center rounded-lg border border-dashed border-gray-800 text-[9px] text-gray-600"
      >
        {seatPosition + 1} · empty
      </div>
    );
  }

  const isWinner = wonRank || wonJewel;
  const statusLabel = STATUS_LABEL[player.status];
  const out = player.status === 'FOLDED' || player.status === 'SITTING_OUT';
  const specials = [specialOfCard(player.holeCards?.[0]), specialOfCard(player.holeCards?.[1])];

  return (
    <div
      data-testid="player-row"
      data-player-id={player.id}
      data-seat={seatPosition + 1}
      data-current-turn={player.isCurrentTurn ? 'true' : 'false'}
      data-status={player.status}
      data-chip={player.hasDealer ? 'true' : 'false'}
      className={`relative flex-1 min-h-0 min-w-0 flex flex-col justify-center gap-0.5 px-1 py-0.5 rounded-lg border ${
        isWinner
          ? 'border-yellow-400 bg-yellow-900/30'
          : player.isCurrentTurn
          ? 'border-yellow-400 bg-yellow-950/40 ring-1 ring-yellow-400/70'
          : 'border-gray-800 bg-gray-900/50'
      } ${out ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center gap-0.5 min-w-0 leading-none">
        <span className="text-[8px] font-bold text-gray-500 flex-shrink-0">{seatPosition + 1}</span>
        <span className={`text-[11px] font-semibold truncate ${isYou ? 'text-yellow-300' : 'text-white'}`}>
          {isYou ? 'You' : player.username}
        </span>
        {!player.isConnected && <span className="text-[8px] text-red-500 flex-shrink-0">⚠</span>}
      </div>
      <div className="flex items-center gap-0.5 leading-none min-w-0">
        <span className="text-[10px] text-yellow-400 font-semibold">{formatChips(player.chipCount)}</span>
        {statusLabel && <span className="text-[8px] text-gray-500 truncate">{statusLabel}</span>}
      </div>
      <div className="flex items-center gap-0.5">
        {/* Each special on its own card's side (left card's to the left, right card's to the right). */}
        {specials[0] && <SpecialCard key="special-0" special={specials[0]} showName={false} className="w-[22px]" />}
        {(player.holeCards ?? [null, null]).slice(0, 2).map((card, i) => (
          <PlayingCard key={i} card={card ?? undefined} faceDown={!card} className="w-[22px]" />
        ))}
        {specials[1] && <SpecialCard key="special-1" special={specials[1]} showName={false} className="w-[22px]" />}
      </div>
      {/* Corner badges: the 1st chip, wins, and the bet this round. */}
      <div className="absolute -top-1 -right-1 flex gap-0.5 text-[9px] leading-none">
        {player.hasDealer && (
          <span title="Acts first this game" className="px-0.5 rounded-full bg-yellow-500 text-black text-[7px] font-bold">
            1st
          </span>
        )}
        {wonRank && <span title="Won Best Hand">🏆</span>}
        {wonJewel && <span title="Won Best Color">💎</span>}
      </div>
      {!!player.currentBet && player.currentBet > 0 && (
        <span className="absolute bottom-0.5 right-0.5 px-1 rounded-full bg-blue-600 text-white text-[8px] font-bold leading-tight">
          {formatChips(player.currentBet)}
        </span>
      )}
    </div>
  );
};

const OpponentList: React.FC<OpponentListProps> = ({ seats, yourSeat, rankWinnerIds, jewelWinnerIds, variant = 'rows' }) => {
  const tiles = variant === 'tiles';
  const column = variant === 'column';
  return (
    <div
      className={
        tiles || column
          ? `flex flex-col h-full ${column ? 'gap-1.5' : 'gap-1'}`
          : 'flex flex-row landscape:flex-col gap-1.5 overflow-x-auto landscape:overflow-x-visible landscape:overflow-y-auto h-full pb-1 landscape:pb-0 landscape:pr-1'
      }
    >
      {seats.map(({ seatPosition, player }) => (
        <OpponentRow
          key={seatPosition}
          seatPosition={seatPosition}
          player={player}
          wonRank={!!player && !!rankWinnerIds?.has(player.id)}
          wonJewel={!!player && !!jewelWinnerIds?.has(player.id)}
          isYou={seatPosition === yourSeat}
          compact={tiles}
          fill={column}
        />
      ))}
    </div>
  );
};

export default OpponentList;
